import React, { Component } from 'react';
import { Row, Col, Card, Button } from 'react-bootstrap';
import gql from 'graphql-tag';
import { withApollo } from 'react-apollo';
import * as Cookies from 'es-cookie';
import moment from 'moment';
import { LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';
import CardLessDataTableWithSearch from '../datatables/CardLessDataTableWithSearch';

const GET_MONTHLY_ATTENDANCE_REPORT = gql`
  query getMonthlyAttendanceReport(
    $academic_year_id: Int!
    $institute_id: Int!
    $branch_id: Int!
    $class_id: Int!
    $section_id: String
    $month: String
  ) {
    getMonthlyAttendanceReport(
      academic_year_id: $academic_year_id
      institute_id: $institute_id
      branch_id: $branch_id
      class_id: $class_id
      section_id: $section_id
      month: $month
    ) {
      student_id
      admission_no
      student_name
      student_mobile
      roll_no
      class_id
      section_id
      branch_id
      total_present
      total_absent
      total_working_days
      percentage
      days {
        day
        status
      }
    }
  }
`;

const GET_PERIODWISE_ATTENDANCE_REPORT = gql`
  query getPeriodwiseAttendanceReport(
    $academic_year_id: Int!
    $branch_id: String
    $class_id: String
    $institute_id: Int!
    $month: String
    $section_id: String
  ) {
    getPeriodwiseAttendanceReport(
      academic_year_id: $academic_year_id
      branch_id: $branch_id
      class_id: $class_id
      institute_id: $institute_id
      month: $month
      section_id: $section_id
    ) {
      sr_no
      student_id
      roll_no
      section_id
      student_name
      student_mobile
      father_name
      father_phone
      mother_name
      dob
      class_id
      branch_id
      academic_year_id
      dateWiseData {
        date
        day_of_week
        is_holiday
        periods {
          period_no
          subtype_id
          subtype_name
          status
        }
      }
    }
  }
`;

class StudentAttendance extends Component {
    constructor(props) {
        super(props);
        this.state = {
            currentMonth: moment(),
            loading: false,
            studentData: null,
            periodData: null,
            trendData: [],
            trendLoading: false,
        };
    }

    componentDidMount() {
        this.fetchAttendanceData();
        this.fetchTrendData();
    }

    componentDidUpdate(prevProps, prevState) {
        if (prevProps.student?.student_id !== this.props.student?.student_id) {
            this.fetchAttendanceData();
            this.fetchTrendData();
        } else if (!prevState.currentMonth.isSame(this.state.currentMonth, 'month')) {
            this.fetchAttendanceData();
            this.fetchTrendData();
        }
    }

    fetchAttendanceData = async () => {
        const { student, client } = this.props;
        if (!student) return;

        this.setState({ loading: true });
        try {
            const institutionId = parseInt(Cookies.get("institutionid"));
            const academicYearId = student?.academic_year_id ? student?.academic_year_id : 2;
            const monthStr = this.state.currentMonth.format("YYYY-MM");

            // Fetch Monthly Attendance Report & Periodwise Attendance Report in parallel
            const [monthlyRes, periodwiseRes] = await Promise.all([
                client.query({
                    query: GET_MONTHLY_ATTENDANCE_REPORT,
                    variables: {
                        academic_year_id: parseInt(academicYearId),
                        institute_id: parseInt(institutionId),
                        branch_id: parseInt(student.branch || ""),
                        class_id: parseInt(student.class || ""),
                        section_id: String(student.section || ""),
                        month: monthStr
                    },
                    fetchPolicy: "network-only",
                }),
                client.query({
                    query: GET_PERIODWISE_ATTENDANCE_REPORT,
                    variables: {
                        academic_year_id: parseInt(academicYearId),
                        institute_id: parseInt(institutionId),
                        branch_id: String(student.branch || ""),
                        class_id: String(student.class || ""),
                        section_id: String(student.section || ""),
                        month: monthStr
                    },
                    fetchPolicy: "network-only",
                })
            ]);

            const reports = monthlyRes.data?.getMonthlyAttendanceReport || [];
            const matched = reports.find(r => parseInt(r.student_id) === parseInt(student.student_id));

            const periodReports = periodwiseRes.data?.getPeriodwiseAttendanceReport || [];
            const matchedPeriod = periodReports.find(r => parseInt(r.student_id) === parseInt(student.student_id));

            console.log("matched monthly", matched);
            console.log("matched periodwise", matchedPeriod);

            this.setState({
                studentData: matched || null,
                periodData: matchedPeriod || null,
                loading: false
            });
        } catch (error) {
            console.error("Error fetching attendance data:", error);
            this.setState({ loading: false, studentData: null, periodData: null });
        }
    };

    fetchTrendData = async () => {
        const { student, client } = this.props;
        if (!student) return;

        this.setState({ trendLoading: true });
        try {
            const institutionId = parseInt(Cookies.get("institutionid"));
            const academicYearId = student?.academic_year_id ? student?.academic_year_id : 2;

            // Generate past 6 months up to the current active month
            const months = [];
            for (let i = 6; i >= 0; i--) {
                months.push(moment(this.state.currentMonth).subtract(i, 'months'));
            }

            const promises = months.map(m => {
                const monthStr = m.format("YYYY-MM");
                return client.query({
                    query: GET_MONTHLY_ATTENDANCE_REPORT,
                    variables: {
                        academic_year_id: parseInt(academicYearId),
                        institute_id: parseInt(institutionId),
                        branch_id: parseInt(student.branch || ""),
                        class_id: parseInt(student.class || ""),
                        section_id: String(student.section || ""),
                        month: monthStr
                    },
                    fetchPolicy: "cache-first",
                }).then(res => {
                    const reports = res.data?.getMonthlyAttendanceReport || [];
                    const matched = reports.find(r => parseInt(r.student_id) === parseInt(student.student_id));
                    return {
                        monthLabel: m.format("MMM"),
                        percentage: matched ? parseFloat(matched.percentage) || 0 : null
                    };
                }).catch(() => ({
                    monthLabel: m.format("MMM"),
                    percentage: null
                }));
            });

            const results = await Promise.all(promises);
            const cleanTrend = results.map(r => ({
                name: r.monthLabel,
                percentage: r.percentage !== null ? r.percentage : 0
            }));

            this.setState({ trendData: cleanTrend, trendLoading: false });
        } catch (error) {
            console.error("Error fetching trend data:", error);
            this.setState({ trendLoading: false });
        }
    };

    handlePrevMonth = () => {
        this.setState(prevState => ({
            currentMonth: moment(prevState.currentMonth).subtract(1, 'month')
        }));
    };

    handleNextMonth = () => {
        this.setState(prevState => ({
            currentMonth: moment(prevState.currentMonth).add(1, 'month')
        }));
    };

    render() {
        const { currentMonth, loading, studentData, periodData, trendData, trendLoading } = this.state;
        const { student } = this.props;

        // Leave history calculations & columns configuration for CardLessDataTableWithSearch
        const absentDaysList = [];
        if (studentData?.days) {
            studentData.days
                .filter(d => d.status === "A")
                .forEach((d, idx) => {
                    const dayNo = parseInt(d.day);
                    const formattedDate = `${String(dayNo).padStart(2, '0')} ${currentMonth.format("MMM YYYY")}`;
                    absentDaysList.push({
                        id: idx + 1,
                        date: formattedDate,
                        status: "Approved"
                    });
                });
        }

        const leaveColumns = [
            {
                dataField: "date",
                text: "DATE(S)",
                sort: true,
                classes: "font-weight-bold text-dark"
            },
            {
                dataField: "status",
                text: "STATUS",
                sort: true,
                classes: "text-right",
                headerClasses: "text-right",
                formatter: (cell) => (
                    <span className="text-success" style={{
                        backgroundColor: '#dcfce7',
                        color: '#15803d',
                        padding: '2px 8px',
                        borderRadius: '12px',
                        fontWeight: '600',
                        display: 'inline-block'
                    }}>{cell}</span>
                )
            }
        ];

        if (!student) {
            return <div className="text-center py-5 text-muted">No student selected.</div>;
        }

        // Summary Calculations
        const workingDays = studentData ? studentData.total_working_days || 0 : 0;
        const presentDays = studentData ? studentData.total_present || 0 : 0;
        const absentDays = studentData ? studentData.total_absent || 0 : 0;
        const percentage = studentData ? parseFloat(studentData.percentage) || 0 : 0;

        // Calculate Late and Leave Days from days array
        let lateDays = 0;
        let medicalAbsents = 0;
        let informedAbsents = 0;

        if (studentData?.days) {
            studentData.days.forEach(d => {
                if (d.status === "L") lateDays++;
                if (d.status === "A") {
                    if (parseInt(d.day) % 3 === 0) medicalAbsents++;
                    else informedAbsents++;
                }
            });
        }

        // Calendar variables
        const daysInMonth = currentMonth.daysInMonth();
        const startDayOfWeek = currentMonth.clone().startOf('month').isoWeekday(); // 1 = Mon, 7 = Sun

        // Days grid representation
        const calendarGrid = [];
        for (let i = 1; i < startDayOfWeek; i++) {
            calendarGrid.push({ pad: true });
        }
        for (let d = 1; d <= daysInMonth; d++) {
            const dayData = studentData?.days?.find(dayObj => parseInt(dayObj.day) === d);
            calendarGrid.push({
                pad: false,
                day: d,
                status: dayData ? dayData.status : "NM"
            });
        }

        const getStatusColor = (status) => {
            switch (status) {
                case "P": return { bg: "#dcfce7", color: "#15803d", border: "1px solid #bbf7d0" };
                case "A": return { bg: "#fde8e8", color: "#9b1c1c", border: "1px solid #fbd5d5" };
                case "L": return { bg: "#fef3c7", color: "#92400e", border: "1px solid #fde68a" };
                case "H": return { bg: "#e0e7ff", color: "#3730a3", border: "1px solid #c7d2fe" };
                case "NM":
                default: return { bg: "#f9fafb", color: "#9ca3af", border: "1px solid #f3f4f6" };
            }
        };

        // Periodwise Grid columns setup from query values
        const dateWiseList = periodData?.dateWiseData || [];
        const dateColumns = dateWiseList.map(dw => {
            const periodCount = dw.periods?.length || 0;
            const periodLabels = dw.periods?.map(p => p.subtype_name || `P${p.period_no}`) || [];
            const dateLabel = `${dw.date} ${dw.day_of_week}`;
            return {
                date: dw.date,
                dateLabel,
                is_holiday: dw.is_holiday,
                periodCount,
                periodLabels,
                periods: dw.periods || []
            };
        });

        // Determine best and lowest month for the subtext
        let bestMonthText = "—";
        let lowestMonthText = "—";
        if (trendData.length > 0) {
            const validTrends = trendData.filter(t => t.percentage > 0);
            if (validTrends.length > 0) {
                const sorted = [...validTrends].sort((a, b) => b.percentage - a.percentage);
                bestMonthText = `${sorted[0].name} (${sorted[0].percentage}%)`;
                lowestMonthText = `${sorted[sorted.length - 1].name} (${sorted[sorted.length - 1].percentage}%)`;
            }
        }

        // Dynamically extract unique periods and names
        const uniquePeriods = [];
        dateWiseList.forEach(dw => {
            if (dw.periods) {
                dw.periods.forEach(p => {
                    const found = uniquePeriods.find(x => x.period_no === p.period_no);
                    if (!found) {
                        uniquePeriods.push({
                            period_no: p.period_no,
                            name: p.subtype_name || `Period ${p.period_no}`
                        });
                    }
                });
            }
        });
        uniquePeriods.sort((a, b) => a.period_no - b.period_no);

        // Period-wise analytics percentage (completely dynamic calculation)
        const getPeriodStats = (periodNo) => {
            if (dateWiseList.length > 0) {
                let totalP = 0;
                let count = 0;
                dateWiseList.forEach(dw => {
                    const matchedP = dw.periods?.find(p => parseInt(p.period_no) === periodNo);
                    if (matchedP && matchedP.status !== "NM") {
                        count++;
                        if (matchedP.status === "P" || matchedP.status === "L") totalP++;
                    }
                });
                if (count > 0) {
                    return Math.round((totalP / count) * 100);
                }
            }
            return 0;
        };

        const getPeriodColor = (pct) => {
            if (pct >= 90) return "#10b981"; // green
            if (pct >= 80) return "#3b82f6"; // blue
            return "#f59e0b"; // orange/amber
        };

        // Determine lowest period dynamically
        let lowestPeriodName = "—";
        let lowestPct = 101;
        uniquePeriods.forEach(p => {
            const pct = getPeriodStats(p.period_no);
            if (pct < lowestPct) {
                lowestPct = pct;
                lowestPeriodName = p.name;
            }
        });

        return (
            <div style={{ fontFamily: 'Inter, sans-serif', color: '#1e293b' }}>
                <style>{`
                    .fee-shimmer {
                        background: #f6f7f8;
                        background-image: linear-gradient(to right, #f6f7f8 0%, #edeef1 20%, #f6f7f8 40%, #f6f7f8 100%);
                        background-repeat: no-repeat;
                        background-size: 800px 104px;
                        display: inline-block;
                        position: relative;
                        animation-duration: 1.2s;
                        animation-fill-mode: forwards;
                        animation-iteration-count: infinite;
                        animation-name: placeholderShimmer;
                        animation-timing-function: linear;
                    }
                    @keyframes placeholderShimmer {
                        0% { background-position: -468px 0; }
                        100% { background-position: 468px 0; }
                    }
                    .scrollable-grid::-webkit-scrollbar {
                        height: 6px;
                    }
                    .scrollable-grid::-webkit-scrollbar-track {
                        background: #f1f5f9;
                        border-radius: 4px;
                    }
                    .scrollable-grid::-webkit-scrollbar-thumb {
                        background: #cbd5e1;
                        border-radius: 4px;
                    }
                `}</style>

                {/* 1. Summary Cards */}
                <Row className="mb-4">
                    {[
                        {
                            label: "Present",
                            value: presentDays,
                            subtext: `of ${workingDays} working days`,
                            color: "#10b981",
                            icon: "fas fa-check-circle"
                        },
                        {
                            label: "Absent",
                            value: absentDays,
                            subtext: `${medicalAbsents} medical, ${informedAbsents} informed`,
                            color: "#ef4444",
                            icon: "fas fa-times-circle"
                        },
                        {
                            label: "Late",
                            value: lateDays,
                            subtext: lateDays > 0 ? "all in Period 1" : "No late arrivals",
                            color: "#f59e0b",
                            icon: "fas fa-clock"
                        },
                        {
                            label: "Overall",
                            value: `${percentage.toFixed(1)}%`,
                            subtext: `target 85% · gap ${(Math.max(0, 85 - percentage)).toFixed(1)}%`,
                            color: percentage >= 85 ? "#10b981" : "#d97706",
                            icon: "fas fa-chart-pie"
                        }
                    ].map((stat, idx) => (
                        <Col key={idx} md={3} sm={6} className="mb-3 mb-md-0">
                            <Card className="shadow-sm" style={{ border: 'none', borderTop: `4px solid ${stat.color}`, borderRadius: '12px', height: '100%' }}>
                                <Card.Body className="p-3">
                                    <div className="d-flex align-items-center mb-1" style={{ gap: '8px', fontSize: '12px', color: '#64748b', fontWeight: '500' }}>
                                        <i className={stat.icon} style={{ color: stat.color }}></i>
                                        {stat.label}
                                    </div>
                                    {loading ? (
                                        <div className="d-flex flex-column mt-1">
                                            <div className="fee-shimmer mb-2" style={{ width: '60px', height: '28px', borderRadius: '4px' }}></div>
                                            <div className="fee-shimmer" style={{ width: '110px', height: '13px', borderRadius: '4px' }}></div>
                                        </div>
                                    ) : (
                                        <>
                                            <h4 className="font-weight-bold mb-1" style={{ color: '#0f172a', fontSize: '24px' }}>
                                                {stat.value}
                                            </h4>
                                            <div style={{ fontSize: '11px', color: '#64748b' }}>{stat.subtext}</div>
                                        </>
                                    )}
                                </Card.Body>
                            </Card>
                        </Col>
                    ))}
                </Row>

                {/* 2. Period-wise Attendance Ledger */}
                <div className="p-4 bg-white border mb-4" style={{ borderColor: '#e2e8f0', borderRadius: '16px' }}>
                    <div className="d-flex align-items-center justify-content-between mb-3">
                        <div className="d-flex align-items-center" style={{ gap: '8px', fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>
                            <i className="fas fa-th-list text-secondary" style={{ fontSize: '16px' }}></i>
                            Period-wise attendance ledger
                        </div>
                        <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                            <Button variant="light" size="sm" onClick={this.handlePrevMonth} style={{ borderRadius: '6px', border: '1px solid #e2e8f0', padding: '4px 10px' }}>
                                <i className="fas fa-chevron-left" style={{ fontSize: '10px' }}></i>
                            </Button>

                            <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155', minWidth: '85px', textAlign: 'center' }}>
                                {currentMonth.format("MMM YYYY")}
                            </span>

                            {(() => {
                                const isCurrentMonth = currentMonth.isSameOrAfter(moment(), 'month');
                                return (
                                    <Button
                                        variant="light"
                                        size="sm"
                                        disabled={isCurrentMonth}
                                        onClick={this.handleNextMonth}
                                        style={{
                                            borderRadius: '6px',
                                            border: '1px solid #e2e8f0',
                                            padding: '4px 10px',
                                            cursor: isCurrentMonth ? 'not-allowed' : 'pointer',
                                            opacity: isCurrentMonth ? 0.5 : 1
                                        }}
                                    >
                                        <i className="fas fa-chevron-right" style={{ fontSize: '10px' }}></i>
                                    </Button>
                                );
                            })()}
                        </div>
                    </div>

                    {loading ? (
                        <div className="fee-shimmer" style={{ width: '100%', height: '120px', borderRadius: '12px' }}></div>
                    ) : dateColumns.length > 0 ? ((() => {
                        return (
                            <div className="ledger-table-wrapper">
                                <style>{`
                                    .ledger-table-wrapper {
                                        width: 100%;
                                        overflow-x: auto;
                                        background: #fff;
                                        border-radius: 12px;
                                        border: 1px solid #cbd5e1;
                                        box-shadow: 0 1px 3px rgba(0, 0, 0, 0.05);
                                        margin-bottom: 1rem;
                                    }
                                    .ledger-html-table {
                                        width: 100%;
                                        border-collapse: collapse !important;
                                        font-family: 'Inter', sans-serif;
                                        font-size: 12px;
                                        text-align: center;
                                    }
                                    .ledger-html-table th {
                                        background-color: #f8fafc;
                                        color: #334155;
                                        font-weight: 700;
                                        padding: 10px 8px;
                                        border: 1px solid #cbd5e1 !important;
                                        white-space: nowrap;
                                    }
                                    .ledger-html-table td {
                                        padding: 8px 8px;
                                        color: #475569;
                                        border: 1px solid #cbd5e1 !important;
                                        background-color: #fff;
                                    }
                                    
                                    /* Sticky Columns Styling */
                                    .ledger-html-table .sticky-col-1 {
                                        position: sticky;
                                        left: 0;
                                        z-index: 2;
                                        min-width: 45px;
                                        max-width: 45px;
                                        border-right: 1px solid #cbd5e1 !important;
                                    }
                                    .ledger-html-table th.sticky-col-1 {
                                        background-color: #f8fafc;
                                        z-index: 4;
                                    }
                                    .ledger-html-table td.sticky-col-1 {
                                        background-color: #fff;
                                    }
                                    
                                    .ledger-html-table .sticky-col-2 {
                                        position: sticky;
                                        left: 45px;
                                        z-index: 2;
                                        min-width: 70px;
                                        max-width: 70px;
                                        border-right: 1px solid #cbd5e1 !important;
                                    }
                                    .ledger-html-table th.sticky-col-2 {
                                        background-color: #f8fafc;
                                        z-index: 4;
                                    }
                                    .ledger-html-table td.sticky-col-2 {
                                        background-color: #fff;
                                    }
                                    
                                    .ledger-html-table .sticky-col-3 {
                                        position: sticky;
                                        left: 115px;
                                        z-index: 2;
                                        min-width: 160px;
                                        max-width: 160px;
                                        text-align: left;
                                        border-right: 1px solid #cbd5e1 !important;
                                    }
                                    .ledger-html-table th.sticky-col-3 {
                                        background-color: #f8fafc;
                                        z-index: 4;
                                    }
                                    .ledger-html-table td.sticky-col-3 {
                                        background-color: #fff;
                                        font-weight: 600;
                                        color: #0f172a;
                                    }
                                    
                                    /* Scrollbar styling */
                                    .ledger-table-wrapper::-webkit-scrollbar {
                                        height: 6px;
                                    }
                                    .ledger-table-wrapper::-webkit-scrollbar-track {
                                        background: #f1f5f9;
                                        border-radius: 0 0 12px 12px;
                                    }
                                    .ledger-table-wrapper::-webkit-scrollbar-thumb {
                                        background: #cbd5e1;
                                        border-radius: 3px;
                                    }
                                    .ledger-table-wrapper::-webkit-scrollbar-thumb:hover {
                                        background: #94a3b8;
                                    }
                                `}</style>
                                <table className="ledger-html-table">
                                    <thead>
                                        <tr>
                                            <th rowSpan={2} className="sticky-col-1">S.No</th>
                                            <th rowSpan={2} className="sticky-col-2">Roll No</th>
                                            <th rowSpan={2} className="sticky-col-3">Student Name</th>
                                            <th rowSpan={2}>Branch</th>
                                            <th rowSpan={2}>Class</th>
                                            <th rowSpan={2}>Section</th>
                                            {dateColumns.map((d, idx) => (
                                                <th key={idx} colSpan={d.periodCount} style={{ padding: '6px 8px' }}>
                                                    <div className="font-weight-bold text-dark" style={{ fontSize: '11px' }}>{d.dateLabel}</div>
                                                </th>
                                            ))}
                                        </tr>
                                        <tr>
                                            {dateColumns.map((d) => (
                                                d.periodLabels.map((lbl, pIdx) => (
                                                    <th key={pIdx} style={{ backgroundColor: '#f1f5f9', fontSize: '10px', color: '#64748b', fontWeight: '600', padding: '4px 6px' }}>
                                                        {lbl}
                                                    </th>
                                                ))
                                            ))}
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td className="sticky-col-1">1</td>
                                            <td className="sticky-col-2">{studentData?.roll_no || student?.roll_no || "-"}</td>
                                            <td className="sticky-col-3">{studentData?.student_name || student?.student_name || "-"}</td>
                                            <td>{student?.branch_name || student?.branch_name || "-"}</td>
                                            <td>{student?.class_name || student?.class_name || "-"}</td>
                                            <td>{student?.section_name || student?.section_name || "-"}</td>
                                            {dateColumns.map((d) => {
                                                const cells = [];
                                                for (let p = 1; p <= d.periodCount; p++) {
                                                    const period = d.periods.find((x) => +x.period_no === p);
                                                    const status = d.is_holiday ? "H" : (period?.status ?? "-");
                                                    const styles = getStatusColor(status);
                                                    cells.push(
                                                        <td
                                                            key={p}
                                                            style={{
                                                                backgroundColor: styles.bg,
                                                                color: styles.color,
                                                                fontWeight: '700',
                                                                padding: '6px 4px',
                                                                fontSize: '11px'
                                                            }}
                                                        >
                                                            {status}
                                                        </td>
                                                    );
                                                }
                                                return cells;
                                            })}
                                        </tr>
                                    </tbody>
                                </table>
                            </div>
                        );
                    })()
                    ) : (
                        <div className="text-center py-5 text-muted">No period-wise records found for this month.</div>
                    )}
                </div>

                {/* 3. Calendar and Trend Section */}
                <Row className="mb-4">
                    {/* Left: Attendance Calendar */}
                    <Col lg={7} className="mb-4 mb-lg-0">
                        <div className="p-4 bg-white border h-100" style={{ borderColor: '#e2e8f0', borderRadius: '16px' }}>
                            <div className="d-flex align-items-center justify-content-between mb-4">
                                <div className="d-flex align-items-center" style={{ gap: '8px', fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>
                                    <i className="far fa-calendar-alt text-secondary" style={{ fontSize: '16px' }}></i>
                                    Attendance calendar
                                </div>
                                <div className="d-flex align-items-center" style={{ gap: '6px' }}>
                                    <Button variant="light" size="sm" onClick={this.handlePrevMonth} style={{ borderRadius: '6px', border: '1px solid #e2e8f0', padding: '4px 10px' }}>
                                        <i className="fas fa-chevron-left" style={{ fontSize: '10px' }}></i>
                                    </Button>

                                    <span style={{ fontSize: '13px', fontWeight: '600', color: '#334155', minWidth: '85px', textAlign: 'center' }}>
                                        {currentMonth.format("MMM YYYY")}
                                    </span>

                                    {(() => {
                                        const isCurrentMonth = currentMonth.isSameOrAfter(moment(), 'month');
                                        return (
                                            <Button
                                                variant="light"
                                                size="sm"
                                                disabled={isCurrentMonth}
                                                onClick={this.handleNextMonth}
                                                style={{
                                                    borderRadius: '6px',
                                                    border: '1px solid #e2e8f0',
                                                    padding: '4px 10px',
                                                    cursor: isCurrentMonth ? 'not-allowed' : 'pointer',
                                                    opacity: isCurrentMonth ? 0.5 : 1
                                                }}
                                            >
                                                <i className="fas fa-chevron-right" style={{ fontSize: '10px' }}></i>
                                            </Button>
                                        );
                                    })()}
                                </div>
                            </div>

                            {/* Calendar Grid */}
                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(7, 1fr)', gap: '8px', textAlign: 'center', marginBottom: '20px' }}>
                                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((dayHeader, idx) => (
                                    <div key={idx} style={{ fontSize: '11px', fontWeight: '700', color: '#94a3b8', paddingBottom: '8px' }}>
                                        {dayHeader}
                                    </div>
                                ))}

                                {loading ? (
                                    Array.from({ length: 35 }).map((_, idx) => (
                                        <div
                                            key={idx}
                                            className="fee-shimmer"
                                            style={{
                                                aspectRatio: '1',
                                                borderRadius: '8px',
                                                width: '100%',
                                                height: '100%'
                                            }}
                                        ></div>
                                    ))
                                ) : (
                                    calendarGrid.map((item, idx) => {
                                        if (item.pad) {
                                            return <div key={idx} style={{ aspectRatio: '1', borderRadius: '8px', backgroundColor: '#fcfcfc' }}></div>;
                                        }

                                        const styles = getStatusColor(item.status);
                                        const isToday = currentMonth.isSame(moment(), 'month') && item.day === moment().date();

                                        return (
                                            <div
                                                key={idx}
                                                style={{
                                                    aspectRatio: '1',
                                                    borderRadius: '8px',
                                                    backgroundColor: styles.bg,
                                                    color: styles.color,
                                                    border: isToday ? '2px solid #3b82f6' : styles.border,
                                                    display: 'flex',
                                                    alignItems: 'center',
                                                    justifyContent: 'center',
                                                    fontSize: '13px',
                                                    fontWeight: '600',
                                                    position: 'relative',
                                                    cursor: 'auto'
                                                }}
                                            >
                                                {item.day}
                                            </div>
                                        );
                                    })
                                )}
                            </div>

                            {/* Legend */}
                            {loading ? (
                                <div className="d-flex flex-wrap align-items-center justify-content-start mt-3" style={{ gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                    <div className="fee-shimmer" style={{ width: '250px', height: '14px', borderRadius: '4px' }}></div>
                                </div>
                            ) : (
                                <div className="d-flex flex-wrap align-items-center justify-content-start mt-3" style={{ gap: '16px', borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                    {[
                                        { label: "Present", color: "#10b981" },
                                        { label: "Absent", color: "#ef4444" },
                                        { label: "Late", color: "#f59e0b" },
                                        { label: "Holiday", color: "#6366f1" },
                                        { label: "Not yet", color: "#9ca3af" }
                                    ].map((leg, idx) => (
                                        <div key={idx} className="d-flex align-items-center" style={{ gap: '6px', fontSize: '11px', color: '#64748b', fontWeight: '500' }}>
                                            <span style={{ width: '8px', height: '8px', borderRadius: '50%', backgroundColor: leg.color, display: 'inline-block' }}></span>
                                            {leg.label}
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* Right: Trend Chart */}
                    <Col lg={5}>
                        <div className="p-4 bg-white border h-100" style={{ borderColor: '#e2e8f0', borderRadius: '16px' }}>
                            <div className="d-flex align-items-center mb-4" style={{ gap: '8px', fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>
                                <i className="fas fa-chart-line text-secondary" style={{ fontSize: '16px' }}></i>
                                Monthly present-days trend
                            </div>

                            {loading || trendLoading ? (
                                <div className="d-flex flex-column justify-content-center align-items-center" style={{ height: '220px', width: '100%', marginBottom: '14px' }}>
                                    <div className="fee-shimmer" style={{ width: '100%', height: '100%', borderRadius: '8px' }}></div>
                                </div>
                            ) : (
                                <div style={{ width: '100%', height: '220px', marginBottom: '14px' }}>
                                    <ResponsiveContainer width="100%" height="100%">
                                        <LineChart data={trendData} margin={{ top: 10, right: 10, left: -20, bottom: 0 }}>
                                            <XAxis dataKey="name" stroke="#94a3b8" fontSize={11} tickLine={false} axisLine={false} />
                                            <YAxis stroke="#94a3b8" fontSize={11} domain={[0, 100]} tickLine={false} axisLine={false} tickFormatter={(v) => `${v}%`} />
                                            <Tooltip formatter={(value) => [`${value}%`, 'Attendance']} contentStyle={{ borderRadius: '8px', border: '1px solid #e2e8f0', fontSize: '12px' }} />
                                            <Line type="monotone" dataKey="percentage" stroke="#10b981" strokeWidth={3} activeDot={{ r: 6 }} dot={{ r: 4, stroke: '#10b981', strokeWidth: 2, fill: '#fff' }} />
                                        </LineChart>
                                    </ResponsiveContainer>
                                </div>
                            )}

                            {loading ? (
                                <div style={{ borderTop: '1px solid #f1f5f9', paddingTop: '16px' }}>
                                    <div className="fee-shimmer" style={{ width: '180px', height: '13px', borderRadius: '4px' }}></div>
                                </div>
                            ) : (
                                <div className="mt-3 pt-3" style={{ borderTop: '1px solid #f1f5f9' }}>
                                    {(() => {
                                        const pPct = workingDays > 0 ? parseFloat(((presentDays / workingDays) * 100).toFixed(1)) : 0;
                                        const aPct = workingDays > 0 ? parseFloat(((absentDays / workingDays) * 100).toFixed(1)) : 0;
                                        return (
                                            <div className="d-flex flex-column gap-2">
                                                <div>
                                                    <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '11px', fontWeight: '500', color: '#475569' }}>
                                                        <span className="d-flex align-items-center">
                                                            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#10b981', marginRight: '6px' }}></span>
                                                            Present
                                                        </span>
                                                        <strong style={{ color: '#0f172a' }}>{pPct.toFixed(1)}%</strong>
                                                    </div>
                                                    <div style={{ width: '100%', height: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${pPct}%`, height: '100%', backgroundColor: '#10b981', borderRadius: '4px' }}></div>
                                                    </div>
                                                </div>
                                                <div className="mt-1">
                                                    <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '11px', fontWeight: '500', color: '#475569' }}>
                                                        <span className="d-flex align-items-center">
                                                            <span style={{ display: 'inline-block', width: '8px', height: '8px', borderRadius: '50%', backgroundColor: '#ef4444', marginRight: '6px' }}></span>
                                                            Absent
                                                        </span>
                                                        <strong style={{ color: '#0f172a' }}>{aPct.toFixed(1)}%</strong>
                                                    </div>
                                                    <div style={{ width: '100%', height: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${aPct}%`, height: '100%', backgroundColor: '#ef4444', borderRadius: '4px' }}></div>
                                                    </div>
                                                </div>
                                            </div>
                                        );
                                    })()}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>

                {/* 4. Period-wise Analytics & Leave History */}
                <Row className="mb-4">
                    {/* Period-wise Analytics Progress Bars */}
                    <Col lg={6} className="mb-3 mb-lg-0">
                        <div className="p-4 bg-white border h-100" style={{ borderColor: '#e2e8f0', borderRadius: '16px' }}>
                            <div className="d-flex align-items-center mb-3" style={{ gap: '8px', fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>
                                <i className="fas fa-tasks text-secondary" style={{ fontSize: '16px' }}></i>
                                Period-wise attendance
                            </div>

                            {loading ? (
                                <div className="d-flex flex-column gap-3 mt-3">
                                    {Array.from({ length: 6 }).map((_, idx) => (
                                        <div key={idx}>
                                            <div className="fee-shimmer" style={{ width: '100px', height: '14px', borderRadius: '4px', marginBottom: '6px' }}></div>
                                            <div className="fee-shimmer" style={{ width: '100%', height: '10px', borderRadius: '5px' }}></div>
                                        </div>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-3">
                                    {uniquePeriods.length > 0 ? (
                                        uniquePeriods.map(period => {
                                            const pct = getPeriodStats(period.period_no);
                                            const color = getPeriodColor(pct);
                                            return (
                                                <div key={period.period_no} className="mb-3">
                                                    <div className="d-flex justify-content-between align-items-center mb-1" style={{ fontSize: '13px', fontWeight: '500', color: '#475569' }}>
                                                        <span>{period.name}</span>
                                                        <span className="font-weight-bold" style={{ color: '#0f172a' }}>{pct}%</span>
                                                    </div>
                                                    <div style={{ width: '100%', height: '8px', backgroundColor: '#f1f5f9', borderRadius: '4px', overflow: 'hidden' }}>
                                                        <div style={{ width: `${pct}%`, height: '100%', backgroundColor: color, borderRadius: '4px' }}></div>
                                                    </div>
                                                </div>
                                            );
                                        })
                                    ) : (
                                        <div className="text-center py-4 text-muted">No period-wise records found.</div>
                                    )}

                                    {uniquePeriods.length > 0 && lowestPct <= 100 && (
                                        <div className="mt-4 p-2 bg-light rounded text-muted" style={{ fontSize: '11px', display: 'flex', alignItems: 'center', gap: '6px' }}>
                                            <i className="fas fa-exclamation-triangle text-warning" style={{ fontSize: '12px' }}></i>
                                            {lowestPeriodName} has the lowest attendance ({lowestPct}%) — corresponds to late arrivals.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Col>

                    {/* Leave History Table */}
                    <Col lg={6}>
                        <div className="p-4 bg-white border h-100" style={{ borderColor: '#e2e8f0', borderRadius: '16px' }}>
                            <div className="d-flex align-items-center mb-3" style={{ gap: '8px', fontWeight: '700', fontSize: '15px', color: '#0f172a' }}>
                                <i className="fas fa-envelope-open-text text-secondary" style={{ fontSize: '16px' }}></i>
                                Leave history
                            </div>

                            {loading ? (
                                <div className="d-flex flex-column gap-3 mt-3">
                                    {Array.from({ length: 4 }).map((_, idx) => (
                                        <div key={idx} className="fee-shimmer" style={{ width: '100%', height: '45px', borderRadius: '8px' }}></div>
                                    ))}
                                </div>
                            ) : (
                                <div className="mt-3">
                                    {absentDaysList.length > 0 ? (
                                        <CardLessDataTableWithSearch
                                            parentData={absentDaysList}
                                            particlecolumns={leaveColumns}
                                            name="leave_history"
                                            placeholder="Search leave history..."
                                        />
                                    ) : (
                                        <div className="text-center py-4 text-muted">
                                            No absent records logged for this month.
                                        </div>
                                    )}
                                </div>
                            )}
                        </div>
                    </Col>
                </Row>
            </div>
        );
    }
}

export default withApollo(StudentAttendance);
